import type {
  ButtonBlock as ButtonBlockProps,
  Media,
  MediaBlock as MediaBlockProps,
  Theme,
} from '@/payload-types';
import {
  DefaultNodeTypes,
  SerializedBlockNode,
  SerializedTableCellNode,
  SerializedTableNode,
  SerializedTableRowNode, SerializedTextNode,
} from '@payloadcms/richtext-lexical';
import { CSSProperties, Fragment, JSX } from 'react';

import ButtonBlock from '@/blocks/Button/Component';
import { CodeBlock, CodeBlockProps } from '@/blocks/Code/Component';
import { MediaBlock } from '@/blocks/MediaBlock/Component';
import { CMSLink } from '@/components/Link';
import { MediaComponent } from '@/components/Media';
import { cssStringToObject } from '@/utilities/cssStringToObject';
import {
  ELEMENT_FORMAT_TO_TYPE,
  IS_ALIGN_CENTER,
  IS_ALIGN_JUSTIFY,
  IS_ALIGN_LEFT,
  IS_ALIGN_RIGHT,
  IS_BOLD,
  IS_CODE,
  IS_ITALIC,
  IS_STRIKETHROUGH,
  IS_SUBSCRIPT,
  IS_SUPERSCRIPT,
  IS_UNDERLINE,
} from './nodeFormat';
import { clsx } from 'clsx';
import { Square, SquareCheckBig } from 'lucide-react';

export type NodeTypes =
  | DefaultNodeTypes
  | SerializedTableCellNode
  | SerializedTableNode
  | SerializedTableRowNode
  | SerializedBlockNode<MediaBlockProps | CodeBlockProps | ButtonBlockProps>;

type Props = {
  nodes: NodeTypes[];
  theme?: Theme;
};

export function serializeLexical({ nodes, theme }: Props): JSX.Element {
  if (!nodes?.length) {
    return <Fragment>&nbsp;</Fragment>;
  }

  return (
    <Fragment>
      {nodes?.map((node, index): JSX.Element | null => {
        if (node == null) {
          return null;
        }

        if (node.type === 'text') {
          const styles = cssStringToObject(node.style);

          let text = (
            <span key={index} style={styles}>
              {node.text}
            </span>
          );
          if (node.format & IS_BOLD) {
            text = (
              <strong key={index} style={styles}>
                {text}
              </strong>
            );
          }
          if (node.format & IS_ITALIC) {
            text = (
              <em key={index} style={styles}>
                {text}
              </em>
            );
          }
          if (node.format & IS_STRIKETHROUGH) {
            text = (
              <span key={index} style={{ ...styles, textDecoration: 'line-through' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_ALIGN_LEFT) {
            text = (
              <span key={index} style={{ ...styles, textAlign: 'left' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_ALIGN_CENTER) {
            text = (
              <span key={index} style={{ ...styles, textAlign: 'center' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_ALIGN_RIGHT) {
            text = (
              <span key={index} style={{ ...styles, textAlign: 'right' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_ALIGN_JUSTIFY) {
            text = (
              <span key={index} style={{ ...styles, textAlign: 'justify' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_UNDERLINE) {
            text = (
              <span key={index} style={{ ...styles, textDecoration: 'underline' }}>
                {text}
              </span>
            );
          }
          if (node.format & IS_CODE) {
            text = (
              <code
                key={index}
                style={styles}
                className="text-red-800 bg-gray-50 border border-gray-200 rounded py-0.5 px-1"
              >
                {node.text}
              </code>
            );
          }
          if (node.format & IS_SUBSCRIPT) {
            text = (
              <sub key={index} style={styles}>
                {text}
              </sub>
            );
          }
          if (node.format & IS_SUPERSCRIPT) {
            text = (
              <sup key={index} style={styles}>
                {text}
              </sup>
            );
          }

          return text;
        }

        // Method table
        const generateTable = (nodes: NodeTypes[]): JSX.Element[] | null => {
          if (!nodes || nodes?.length === 0) {
            return null;
          }

          return nodes?.map((node, rowIndex) => {
            switch (node?.type) {
              case 'tablerow':
                return (
                  <tr key={rowIndex} style={{ height: `${node?.height || 45}px` }}>
                    {node?.children?.map(
                      (child: SerializedTableCellNode | SerializedTableRowNode, cellIndex) => {
                        // Check xem nếu mà phần tử đầu tiên của tag tr thì nó sẽ render ra tag tg
                        const isHeaderCell = rowIndex === 0 || cellIndex === 0;
                        return isHeaderCell ? (
                          <th
                            key={`th-${rowIndex}-${cellIndex}`}
                            className="text-left bg-gray-300 p-3 border-1 border-solid border-gray-400"
                          >
                            {generateTable(child.children as NodeTypes[])}
                          </th>
                        ) : (
                          <td key={`td-${rowIndex}-${cellIndex}`} className="p-3 border-1 border-solid border-gray-400">
                            {generateTable(child.children as NodeTypes[])}
                          </td>
                        );
                      },
                    )}
                  </tr>
                );
              default:
                return serializeLexical({ nodes: [node] });
            }
          });
        };

        if (node.type === 'table') {
          return (
            <table key={index} className="block max-w-full overflow-auto">
              <colgroup>
                {node.colWidths?.map((width, colIndex) => (
                  <col key={`${index}-${colIndex}`} style={{ width: `${width}px` }} />
                ))}
              </colgroup>
              <tbody>{generateTable(node.children as NodeTypes[])}</tbody>
            </table>
          );
        }

        if (node?.type === 'horizontalrule') {
          return <hr key={index} className="h-0.5 bg-gray-300 border-0" />;
        }

        if (node?.type === 'upload') {
          return (
            <Fragment key={index}>
              <MediaComponent resource={node?.value as Media} imgClassName='w-auto' />
            </Fragment>
          );
        }

        // NOTE: Hacky fix for
        // https://github.com/facebook/lexical/blob/d10c4e6e55261b2fdd7d1845aed46151d0f06a8c/packages/lexical-list/src/LexicalListItemNode.ts#L133
        // which does not return checked: false (only true - i.e. there is no prop for false)
        const serializedChildrenFn = (node: NodeTypes): JSX.Element | null => {
          if (node.children == null) {
            return null;
          } else {
            if (node?.type === 'list' && node?.listType === 'check') {
              for (const item of node.children) {
                if ('checked' in item) {
                  if (!item?.checked) {
                    item.checked = false;
                  }
                }
              }
            }
            return serializeLexical({ nodes: node.children as NodeTypes[], theme });
          }
        };

        const serializedChildren = 'children' in node ? serializedChildrenFn(node) : '';

        if (node.type === 'block') {
          const block = node.fields;

          const blockType = block?.blockType;

          if (!block || !blockType) {
            return null;
          }

          switch (blockType) {
            case 'mediaBlock':
              return (
                <MediaBlock
                  className="col-start-1 col-span-3"
                  imgClassName="m-auto"
                  key={index}
                  {...block}
                  captionClassName="mx-auto max-w-[48rem]"
                  enableGutter={false}
                  disableInnerContainer={true}
                />
              );
            case 'code':
              return <CodeBlock className="col-start-2" key={index} {...block} />;
            case 'buttonBlock':
              return <ButtonBlock key={index} {...block} theme={theme} />;
            default:
              return null;
          }
        } else {
          const firstChildStyleString = (node.children?.[0] as SerializedTextNode)?.style || "";
          const firstChildStyle = cssStringToObject(firstChildStyleString);

          switch (node.type) {
            case 'linebreak': {
              return (
                <span className="col-start-2" key={index}>
                  {'\n'}
                </span>
              );
            }
            case 'paragraph': {
              const styles: CSSProperties = {};
              if (node.format === ELEMENT_FORMAT_TO_TYPE[IS_ALIGN_LEFT]) {
                styles['textAlign'] = 'left';
              }
              if (node.format === ELEMENT_FORMAT_TO_TYPE[IS_ALIGN_CENTER]) {
                styles['textAlign'] = 'center';
              }
              if (node.format === ELEMENT_FORMAT_TO_TYPE[IS_ALIGN_RIGHT]) {
                styles['textAlign'] = 'right';
              }
              if (node.format === ELEMENT_FORMAT_TO_TYPE[IS_ALIGN_JUSTIFY]) {
                styles['textAlign'] = 'justify';
              }

              styles['paddingInlineStart'] = `calc(${node?.indent * 40}px)`;

              return (
                <p className="col-start-2" key={index} style={styles}>
                  {serializedChildren}
                </p>
              );
            }
            case 'heading': {
              const Tag = node?.tag;
              const styles: CSSProperties = {};
              if (node.format === ELEMENT_FORMAT_TO_TYPE[IS_ALIGN_LEFT]) {
                styles['textAlign'] = 'left';
              }
              if (node.format === ELEMENT_FORMAT_TO_TYPE[IS_ALIGN_CENTER]) {
                styles['textAlign'] = 'center';
              }
              if (node.format === ELEMENT_FORMAT_TO_TYPE[IS_ALIGN_RIGHT]) {
                styles['textAlign'] = 'right';
              }
              if (node.format === ELEMENT_FORMAT_TO_TYPE[IS_ALIGN_JUSTIFY]) {
                styles['textAlign'] = 'justify';
              }

              return (
                <Tag
                  className="col-start-2 max-sm:whitespace-normal"
                  key={index}
                  style={{ ...styles, ...firstChildStyle }}
                >
                  {serializedChildren}
                </Tag>
              );
            }
            case 'list': {
              const Tag = node?.tag;
              return (
                <Tag
                  className={clsx(
                    'list col-start-2 pl-5',
                    node?.tag === 'ol' ? 'list-decimal' : 'list-disc',
                  )}
                  key={index}
                >
                  {serializedChildren}
                </Tag>
              );
            }
            case 'listitem': {
              if (node?.checked != null) {
                return (
                  <li
                    aria-checked={node.checked ? 'true' : 'false'}
                    className={clsx('list-none -ml-5', node.checked && 'line-through')}
                    key={index}
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-to-interactive-role
                    role="checkbox"
                    tabIndex={-1}
                    value={node?.value}
                  >
                    <div className="flex items-center gap-2">
                      {node.checked ? <SquareCheckBig /> : <Square />}
                      {serializedChildren}
                    </div>
                  </li>
                );
              } else {
                return (
                  <li key={index} value={node?.value}>
                    {serializedChildren}
                  </li>
                );
              }
            }
            case 'quote': {
              return (
                <blockquote
                  className="col-start-2 border-l-4 border-gray-300 pl-4 my-4"
                  key={index}
                >
                  {serializedChildren}
                </blockquote>
              );
            }
            case 'link': {
              const fields = node.fields;

              return (
                <CMSLink
                  key={index}
                  newTab={Boolean(fields?.newTab)}
                  reference={fields.doc as any}
                  type={fields.linkType === 'internal' ? 'reference' : 'custom'}
                  url={fields.url}
                >
                  {serializedChildren}
                </CMSLink>
              );
            }

            default:
              return null;
          }
        }
      })}
    </Fragment>
  );
}
